import React, { useEffect, useState } from "react";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Dialog } from "primereact/dialog";
import { InputText } from "primereact/inputtext";
import { InputSwitch } from "primereact/inputswitch";
import { Dropdown } from "primereact/dropdown";
import { Plus, Edit, Trash2 } from "lucide-react";
import { IconTextButton } from "../../components/ButtonComponents";
import { toast } from "react-toastify";
import { useSettingDefinitionStore } from "../../store/settingDefinitionStore";
import { SettingDefinition } from "../../types/settingDefinition";
import { useTableHeader } from "../../hooks/useTableHeader";
import { useDynamicScrollHeight } from "../../hooks/useDynamicScrollHeight";
import { useConfirmModal } from "../../hooks/useConfirmModal";

const initialForm: Partial<SettingDefinition> = {
    tabKey: "", tabLabel: "",
    sectionKey: "", sectionLabel: "",
    fieldKey: "", fieldType: "STRING",
    label: "", description: "",
    targetJson: "customerAppSettings",
    isActive: true
};

export const SettingDefinitionManagement = () => {
    const { data, total, loading, fetchDataDynamic, createItem, updateItem, deleteItem } = useSettingDefinitionStore();
    const scrollHeight = useDynamicScrollHeight(260);

    // YENİ: Projenizdeki useConfirmModal objesinden confirm fonksiyonunu ve modal bileşenini alıyoruz
    const { confirm, modal: confirmModal } = useConfirmModal();

    const [first, setFirst] = useState(0);
    const [rows, setRows] = useState(15);
    const [isDialogVisible, setDialogVisible] = useState(false);
    const [formData, setFormData] = useState<Partial<SettingDefinition>>(initialForm);
    const [isEditing, setIsEditing] = useState(false);

    // Standart Arama ve Filtre Hook'u
    const { header, dynamicFilter } = useTableHeader({
        title: "Ayar Şeması Yönetimi (Süperadmin)",
        placeholder: "Alan adı veya Etiket ara...",
        searchFields: [
            { field: "label", operator: "contains" },
            { field: "fieldKey", operator: "contains" },
            { field: "tabLabel", operator: "contains" }
        ],
        debounceMs: 350,
        actions: (
            <IconTextButton icon={Plus} onClick={() => openDialog()} className="btn-new">
                Yeni Alan Ekle
            </IconTextButton>
        )
    });

    useEffect(() => {
        fetchDataDynamic({
            pageIndex: first / rows + 1,
            pageSize: rows,
            filter: dynamicFilter || undefined // YENİ: Type hatasını çözmek için '|| undefined' eklendi
        });
    }, [first, rows, dynamicFilter]);

    const openDialog = (item?: SettingDefinition) => {
        if (item) {
            setFormData({ ...item });
            setIsEditing(true);
        } else {
            setFormData(initialForm);
            setIsEditing(false);
        }
        setDialogVisible(true);
    };

    const handleSave = async () => {
        if (!formData.fieldKey || !formData.label || !formData.tabKey) {
            toast.warning("Lütfen zorunlu alanları (Sekme, Grup, Alan Key, Etiket) doldurun.");
            return;
        }
        const success = isEditing
            ? await updateItem(formData.id!, formData)
            : await createItem(formData);

        if (success) {
            toast.success("Ayar şeması başarıyla kaydedildi.");
            setDialogVisible(false);
            fetchDataDynamic({ pageIndex: first / rows + 1, pageSize: rows, filter: dynamicFilter || undefined });
        }
    };

    // YENİ: confirm fonksiyonunun await ile beklenen kullanımı
    const handleDelete = async (id: number) => {
        const ok = await confirm("Bu ayar alanını silmek istediğinize emin misiniz? (Firmaların değerleri silinmez ama ekranda gözükmez)", "Silme Onayı");
        if (ok) {
            const success = await deleteItem(id);
            if (success) {
                toast.success("Alan başarıyla silindi.");
                fetchDataDynamic({ pageIndex: first / rows + 1, pageSize: rows, filter: dynamicFilter || undefined });
            }
        }
    };

    return (
        <div className="w-full bg-bg min-h-screen">
            {header}
            <div className="p-4">
                <DataTable
                    value={data || []}
                    lazy
                    paginator
                    first={first}
                    rows={rows}
                    totalRecords={total}
                    onPage={(e) => { setFirst(e.first); setRows(e.rows); }}
                    loading={loading}
                    scrollHeight={scrollHeight}
                    scrollable
                    className="p-datatable-sm"
                    rowHover
                    stripedRows
                    emptyMessage="Kayıt bulunamadı."
                >
                    <Column field="tabLabel" header="Sekme" />
                    <Column field="sectionLabel" header="Grup" />
                    <Column field="label" header="Etiket (Kullanıcı Tarafı)" />
                    <Column field="fieldKey" header="Field Key (Veritabanı)" />
                    <Column field="targetJson" header="JSON Alanı" />
                    <Column field="fieldType" header="Tipi" />
                    <Column field="isActive" header="Durum" body={(r) => r.isActive ? <span className="text-green-500 font-bold">Aktif</span> : <span className="text-red-500 font-bold">Pasif</span>} />
                    <Column body={(row) => (
                        <div className="flex gap-2">
                            <button onClick={() => openDialog(row)} className="p-2 hover:bg-bgAlt text-primary rounded-lg transition-colors">
                                <Edit size={16} />
                            </button>
                            <button onClick={() => handleDelete(row.id)} className="p-2 hover:bg-red-50 text-red-500 rounded-lg transition-colors">
                                <Trash2 size={16} />
                            </button>
                        </div>
                    )} />
                </DataTable>
            </div>

            {/* YENİ: Confirm Modalının UI'a bindirilmesi */}
            {confirmModal}

            <Dialog
                header={isEditing ? "Ayarı Düzenle" : "Yeni Ayar Alanı Ekle"}
                visible={isDialogVisible}
                style={{ width: '600px' }}
                onHide={() => setDialogVisible(false)}
                footer={
                    <div className="flex justify-end gap-2 mt-4">
                        <button onClick={() => setDialogVisible(false)} className="px-4 py-2 border border-borderColor rounded-lg hover:bg-bgAlt transition-colors font-medium text-text opacity-80">İptal</button>
                        <button onClick={handleSave} className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primaryHover transition-colors font-bold shadow-md">Kaydet</button>
                    </div>
                }
            >
                <div className="grid grid-cols-2 gap-4 mt-2 text-sm text-text">
                    <div className="flex flex-col gap-1">
                        <label className="font-bold opacity-80">Sekme Key <span className="text-red-500">*</span></label>
                        <InputText value={formData.tabKey || ""} onChange={(e) => setFormData({ ...formData, tabKey: e.target.value })} placeholder="Örn: sales_app" className="p-inputtext-sm w-full" />
                    </div>
                    <div className="flex flex-col gap-1">
                        <label className="font-bold opacity-80">Sekme Başlığı</label>
                        <InputText value={formData.tabLabel || ""} onChange={(e) => setFormData({ ...formData, tabLabel: e.target.value })} placeholder="Örn: Saha Satış Uygulaması" className="p-inputtext-sm w-full" />
                    </div>

                    <div className="flex flex-col gap-1">
                        <label className="font-bold opacity-80">Grup Key <span className="text-red-500">*</span></label>
                        <InputText value={formData.sectionKey || ""} onChange={(e) => setFormData({ ...formData, sectionKey: e.target.value })} placeholder="Örn: order_rules" className="p-inputtext-sm w-full" />
                    </div>
                    <div className="flex flex-col gap-1">
                        <label className="font-bold opacity-80">Grup Başlığı</label>
                        <InputText value={formData.sectionLabel || ""} onChange={(e) => setFormData({ ...formData, sectionLabel: e.target.value })} placeholder="Örn: Sipariş Kuralları" className="p-inputtext-sm w-full" />
                    </div>

                    <div className="flex flex-col gap-1">
                        <label className="font-bold opacity-80">Alan Key <span className="text-red-500">*</span></label>
                        <InputText value={formData.fieldKey || ""} onChange={(e) => setFormData({ ...formData, fieldKey: e.target.value })} placeholder="Örn: allowNegativeStock" className="p-inputtext-sm w-full" />
                    </div>
                    <div className="flex flex-col gap-1">
                        <label className="font-bold opacity-80">Alan Etiketi (Kullanıcıda Gözüken) <span className="text-red-500">*</span></label>
                        <InputText value={formData.label || ""} onChange={(e) => setFormData({ ...formData, label: e.target.value })} placeholder="Örn: Eksi Stok İzni" className="p-inputtext-sm w-full" />
                    </div>

                    <div className="col-span-2 flex flex-col gap-1">
                        <label className="font-bold opacity-80">Açıklama</label>
                        <InputText value={formData.description || ""} onChange={(e) => setFormData({ ...formData, description: e.target.value })} placeholder="Örn: Stok yetersiz olsa bile sipariş alınmasına izin verilsin mi?" className="p-inputtext-sm w-full" />
                    </div>

                    <div className="flex flex-col gap-1">
                        <label className="font-bold opacity-80">Hedef JSON Verisi</label>
                        <Dropdown
                            value={formData.targetJson}
                            options={[
                                { label: 'Müşteri App JSON', value: 'customerAppSettings' },
                                { label: 'Firma/Saha App JSON', value: 'firmAppSettings' },
                                { label: 'Ana Kök Dizin (ROOT)', value: 'ROOT' }
                            ]}
                            onChange={(e) => setFormData({ ...formData, targetJson: e.value })}
                            className="p-inputtext-sm w-full"
                        />
                    </div>

                    <div className="flex flex-col gap-1">
                        <label className="font-bold opacity-80">Veri Tipi</label>
                        <Dropdown
                            value={formData.fieldType}
                            options={[
                                { label: 'Aç/Kapat (BOOLEAN)', value: 'BOOLEAN' },
                                { label: 'Sayısal (NUMBER)', value: 'NUMBER' },
                                { label: 'Metin (STRING)', value: 'STRING' }
                            ]}
                            onChange={(e) => setFormData({ ...formData, fieldType: e.value })}
                            className="p-inputtext-sm w-full"
                        />
                    </div>

                    <div className="col-span-2 flex items-center justify-between mt-2 p-3 bg-bgAlt border border-borderColor rounded-lg">
                        <label className="font-bold opacity-80 cursor-pointer" htmlFor="isActiveSwitch">Aktif (Kullanıcılar Görebilir mi?)</label>
                        <InputSwitch inputId="isActiveSwitch" checked={formData.isActive || false} onChange={(e) => setFormData({ ...formData, isActive: e.value })} />
                    </div>
                </div>
            </Dialog>
        </div>
    );
};
